/**
 * Phase 7 · ContentRenderer
 * Renders raw `contentRef.html` strings with full visual fidelity AND working
 * post-render JS handlers (flip cards · wizards · inline QC · sample reveal).
 *
 * Per Genie ack 2 MAY 2026 · pattern (a) = CSS port + post-render handlers.
 * 4 of 8 element categories are pure CSS (glossary tooltip · ISTQB plate ·
 * senior-extension <details> · intel/recon/sitrep boxes) and need no handler
 * — they work via `dangerouslySetInnerHTML` alone.
 *
 * Persisted state (per Progress · ProgressContext):
 * - `wizardState[wizardId].activeStep` · sticks across re-renders + reloads
 * - `inlineQc[blockId]` · sticks selection + correct/wrong status
 * Sample reveal state is session-only (per spec pedagogy · re-attempt on revisit).
 */
import { useEffect, useMemo, useRef } from 'react';
import { useProgress } from '@/contexts/ProgressContext';
import { initFlipCards } from '@/lib/interactive/flipCards';
import { initFlipTriple } from '@/lib/interactive/flipTriple';
import { initVerdictProbe } from '@/lib/interactive/verdictProbe';
import { initWizards, syncWizardDom } from '@/lib/interactive/wizards';
import { initInlineQc, syncInlineQcDom } from '@/lib/interactive/inlineQc';
import { initSampleReveal } from '@/lib/interactive/sampleReveal';
import { initMythCards } from '@/lib/interactive/mythCards';
import { initSorters } from '@/lib/interactive/sorter';
import { initKanbanPull } from '@/lib/interactive/kanbanPull';
import { initPhaseChains } from '@/lib/interactive/phaseChain';
import { initRoleIcons } from '@/lib/interactive/roleIcons';
import { initDrawers } from '@/lib/interactive/drawers';
import { initGlossary } from '@/lib/interactive/glossary';
import { initCopyBlocks } from '@/lib/interactive/copyBlocks';
import { initBuddyNotes } from '@/lib/interactive/buddyNotes';
import { initTabDecks } from '@/lib/interactive/tabDecks';
import { initScDeck } from '@/lib/interactive/scDeck';
import { initStoryBuilder } from '@/lib/interactive/storyBuilder';
import { initStructDoc } from '@/lib/interactive/structDoc';
import { initDecompose } from '@/lib/interactive/decompose';
import { initRepair } from '@/lib/interactive/repair';
import { initCalibrate } from '@/lib/interactive/calibrate';
import { initBugHunt } from '@/lib/interactive/bugHunt';
import { initBvaPick } from '@/lib/interactive/bvaPick';
import { initChain } from '@/lib/interactive/chain';
import { initDragSort } from '@/lib/interactive/dragSort';
import { initCodeAlong } from '@/lib/interactive/codeAlong';
import { initCodeFormat } from '@/lib/interactive/codeFormat';
import { initPyRun } from '@/lib/interactive/pyRun';
import { initPoBuild } from '@/lib/interactive/poBuild';
import { initRefactorFlow } from '@/lib/interactive/refactorFlow';
import { initPromptForge } from '@/lib/interactive/promptForge';
import { initPipelineRun } from '@/lib/interactive/pipelineRun';
import { initLogTriage } from '@/lib/interactive/logTriage';
import { initTranslate } from '@/lib/interactive/translate';
import { initCompareGrid } from '@/lib/interactive/compareGrid';
import { createRoot, type Root } from 'react-dom/client';
import { AtelierEmbed } from './AtelierEmbed';
import type { InlineQcState, WizardBlockState } from '@/types';

const EMPTY_WIZARD_STATE: Readonly<Record<string, WizardBlockState>> = Object.freeze({});
const EMPTY_INLINE_QC_STATE: Readonly<Record<string, InlineQcState>> = Object.freeze({});

interface ContentRendererProps {
  /** Raw HTML string from `ticket.contentRef.html`. Author content · trusted source. */
  html: string;
  /** Ticket id used to scope persisted wizard/QC state. */
  ticketId: string;
  /** Optional className passed to the wrapper div. */
  className?: string;
}

export function ContentRenderer({ html, ticketId, className }: ContentRendererProps) {
  const ref = useRef<HTMLDivElement>(null);
  const atelierRoots = useRef<Map<HTMLElement, Root>>(new Map());
  const atelierAlive = useRef(true);
  const { progress, setWizardStep, setInlineQc } = useProgress();
  const ticket = progress.tickets[ticketId];
  const wizardState = useMemo(
    () => ticket?.wizardState ?? EMPTY_WIZARD_STATE,
    [ticket?.wizardState],
  );
  const inlineQcState = useMemo(
    () => ticket?.inlineQc ?? EMPTY_INLINE_QC_STATE,
    [ticket?.inlineQc],
  );

  // Install handlers once per html / ticketId. Setters are stable from Provider.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const cleanups = [
      initFlipCards(node),
      initFlipTriple(node),
      initVerdictProbe(node),
      initSampleReveal(node),
      initMythCards(node),
      initSorters(node),
      initKanbanPull(node),
      initPhaseChains(node),
      initRoleIcons(node),
      initDrawers(node),
      initGlossary(node),
      initCopyBlocks(node),
      initBuddyNotes(node),
      initTabDecks(node),
      initScDeck(node),
      initStoryBuilder(node),
      initStructDoc(node),
      initDecompose(node),
      initRepair(node),
      initCalibrate(node),
      initBugHunt(node),
      initBvaPick(node),
      initChain(node),
      initDragSort(node),
      initCodeAlong(node),
      initCodeFormat(node),
      initPyRun(node),
      initPoBuild(node),
      initRefactorFlow(node),
      initPromptForge(node),
      initPipelineRun(node),
      initLogTriage(node),
      initTranslate(node),
      initCompareGrid(node),
      initWizards(node, (wizardId, step) => setWizardStep(ticketId, wizardId, step)),
      initInlineQc(node, (blockId, state) => setInlineQc(ticketId, blockId, state)),
    ];
    return () => cleanups.forEach((fn) => fn());
  }, [html, ticketId, setWizardStep, setInlineQc]);

  // Sync persisted state to DOM after every render where state changed.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    syncWizardDom(node, wizardState);
    syncInlineQcDom(node, inlineQcState);
  }, [wizardState, inlineQcState]);

  // Variant B bridge · portal-mount the REAL Артель sandbox into `.atelier-embed`
  // placeholders (data-screen / -scenario / -sandbox / -intro / -title). Reuses the
  // same AtelierEmbed the homework uses (iframe /atelier.html?…&sandbox-mode=on).
  // Roots kept in a ref and reused across StrictMode double-invokes (re-render, not
  // re-createRoot); hosts that disappear on html change are unmounted. No existing
  // ticket carries this class → zero impact until a hunt opts in.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    atelierAlive.current = true; // cancel any pending teardown (StrictMode remount)
    const map = atelierRoots.current;
    const present = new Set<HTMLElement>();
    node.querySelectorAll<HTMLElement>('.atelier-embed').forEach((host) => {
      present.add(host);
      let root = map.get(host);
      if (!root) {
        root = createRoot(host);
        map.set(host, root);
      }
      root.render(
        <AtelierEmbed
          embed={{
            screen: host.dataset.screen || 'marketplace',
            scenarioId: host.dataset.scenario || undefined,
            sandbox: host.dataset.sandbox !== 'off',
            intro: host.dataset.intro || undefined,
          }}
          title={host.dataset.title || undefined}
          launchOnly
        />,
      );
    });
    for (const [host, root] of map) {
      if (!present.has(host)) {
        const r = root;
        queueMicrotask(() => r.unmount());
        map.delete(host);
      }
    }
  }, [html, ticketId]);

  // Unmount all Артель roots when the renderer unmounts — but not on StrictMode's
  // simulated unmount, which is immediately followed by a remount that re-runs the
  // mount effect (flipping atelierAlive back on). Defer + skip if remounted; else the
  // deferred unmount wipes the freshly re-created root's host DOM (dev-only blank embed).
  useEffect(
    () => () => {
      atelierAlive.current = false;
      queueMicrotask(() => {
        if (atelierAlive.current) return; // remounted → keep roots
        const map = atelierRoots.current;
        map.forEach((root) => root.unmount());
        map.clear();
      });
    },
    [],
  );

  return (
    <div
      ref={ref}
      className={className ?? 'content-rendered'}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
